import { registerSW } from 'virtual:pwa-register'
import { watch } from 'vue'
import { useConferenceLifecycle } from '../stores/conferenceLifecycle'

const UPDATE_INTERVAL = 60 * 60 * 1000 // 1 час

// Ветка installing может ещё качать бандл — таймер лишь страхует «не застрять
// навсегда», это не бюджет на установку. Ветка waiting: сборка готова, reload
// прилетает за доли секунды, короткий таймер только на случай застрявшего reload.
const INSTALLING_WATCHDOG_MS = 12000
const WAITING_WATCHDOG_MS = 3000

// Регистрация SW и функция применения обновления — модульные синглтоны: SW у
// приложения ровно один, а барьер перед входом (VideoLayout) должен видеть его
// состояние из другого компонента. Заполняются при регистрации в useServiceWorker.
let swRegistration: ServiceWorkerRegistration | undefined
let updateServiceWorker: ((reload?: boolean) => Promise<void>) | undefined

// Готовится ли новая сборка прямо сейчас: worker ставится (installing) или уже
// ждёт активации (waiting). Барьер перед входом спрашивает это на нажатие «Войти».
// installing/waiting без живого контроллера — это ПЕРВАЯ установка SW у нового
// посетителя (обновлять не на что): барьер там не нужен, идёт обычный вход.
export function hasPendingUpdate(): boolean {
  if (!navigator.serviceWorker?.controller) return false
  return !!(swRegistration?.installing || swRegistration?.waiting)
}

// Применяет готовящуюся сборку и перезагружает вкладку, либо (провал/зависание)
// вызывает onFallback — обычный вход на текущей сборке. Reload делает не сам
// updateSW(true): он лишь шлёт SKIP_WAITING, а перезагрузку взводит workbox —
// слушатель `controlling`, повешенный при событии `waiting` (см. register.js
// showSkipWaitingPrompt). Ждём installed → только тогда просим активацию.
//
// Исход одноразовый (settled): кто первым — statechange:'redundant', watchdog
// или сам reload (страница умирает) — тот забирает решение, остальные no-op.
// statechange:'installed' НЕ финализирует: там мы лишь просим активацию и
// перевзводим watchdog на короткий срок (состояние становится тем же, что в
// ветке waiting), см. onStateChange ниже.
// Возвращает cancel() для размонтирования: после него ни reload, ни fallback от
// этого слушателя уже невозможны. Сам слушатель барьера reload не вызывает —
// перезагрузку взводит штатный onNeedRefresh (workbox waiting →
// showSkipWaitingPrompt). На этапе входа (после fallback, пока lifecycle.join()
// ещё не вызван и hasJoined=false) onNeedRefresh теоретически ещё может
// перезагрузить; посреди активного звонка — нет, там hasJoined даёт отсрочку.
export function applyPendingUpdate(onFallback: () => void): () => void {
  const reg = swRegistration
  const applyUpdate = updateServiceWorker
  if (!reg || !applyUpdate) {
    onFallback()
    return () => {}
  }

  let settled = false
  let watchdog: number | undefined
  let worker: ServiceWorker | null = null
  let onStateChange: (() => void) | null = null

  const detach = () => {
    if (watchdog !== undefined) clearTimeout(watchdog)
    if (worker && onStateChange) worker.removeEventListener('statechange', onStateChange)
  }

  // Одноразовый исход: первый сработавший фиксирует settled и снимает слушатель
  // с таймером; поздний statechange после этого — no-op, слушатель уже снят.
  const settle = (finalize: () => void) => {
    if (settled) return
    settled = true
    detach()
    finalize()
  }

  // Для размонтирования: гасит исход без действий (ни reload, ни fallback).
  const cancel = () => settle(() => {})

  if (reg.waiting) {
    void applyUpdate(true)
    watchdog = window.setTimeout(() => settle(onFallback), WAITING_WATCHDOG_MS)
    return cancel
  }

  worker = reg.installing
  if (!worker) {
    onFallback()
    return () => {}
  }

  const installing = worker
  onStateChange = () => {
    if (installing.state === 'redundant') {
      settle(onFallback)
      return
    }
    if (installing.state !== 'installed') return

    // Install дошёл до installed: сборка готова. Дальше состояние идентично
    // ветке waiting — просим активацию (SKIP_WAITING) и ждём reload от workbox.
    // НЕ финализируем: снимаем слушатель и длинный (installing) watchdog, затем
    // взводим короткий (waiting) как страховку от застрявшего reload. settled
    // остаётся false, поэтому cancel() при размонтировании ещё погасит новый
    // watchdog, а окончательный исход зафиксирует reload либо этот fallback.
    detach()
    void applyUpdate(true)
    watchdog = window.setTimeout(() => settle(onFallback), WAITING_WATCHDOG_MS)
  }
  installing.addEventListener('statechange', onStateChange)
  watchdog = window.setTimeout(() => settle(onFallback), INSTALLING_WATCHDOG_MS)
  return cancel
}

/**
 * Управляет обновлением Service Worker.
 *
 * Стратегия:
 * - Проверяем обновление по событиям (возврат из фона, возврат сети, выход из
 *   звонка, переподключение сигналинга), а часовой таймер — лишь страховка для
 *   вкладки, которая просто висит
 * - Каждая проверка идёт только при наличии сети (`navigator.onLine`)
 * - Если пользователь в звонке — откладываем перезагрузку до выхода
 */
export function useServiceWorker() {
  let pendingUpdate = false

  const lifecycle = useConferenceLifecycle()

  // Единая точка проверки обновления: спрашиваем сервер, только если есть сеть.
  // onLine ≠ досягаемость сервера, поэтому update() может отклониться — гасим reject
  const checkForUpdate = (reason: string) => {
    if (!swRegistration || !navigator.onLine) return
    console.log(`[SW] Update check (${reason})`)
    void swRegistration.update().catch((err) => console.warn('[SW] Update check failed', err))
  }

  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, reg) {
      if (!reg) return
      swRegistration = reg
      console.log('[SW] SW registered')

      // Часовой таймер — страховка для давно открытой вкладки
      setInterval(() => checkForUpdate('periodic'), UPDATE_INTERVAL)

      // Возврат из фона (критично для iOS PWA)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate('visibility')
      })

      // Возврат из bfcache: на iOS PWA основной путь пробуждения,
      // visibilitychange там срабатывает не всегда
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) checkForUpdate('pageshow')
      })

      // Сеть вернулась — предыдущие проверки могли не пройти
      window.addEventListener('online', () => checkForUpdate('online'))
    },
    onNeedRefresh() {
      console.log('[SW] New version available')

      // Если в звонке — отложить перезагрузку
      if (lifecycle.hasJoined) {
        console.log('[SW] In call, deferring update')
        pendingUpdate = true
        return
      }

      // Не в звонке — обновляем сразу
      console.log('[SW] Applying update')
      updateSW(true)
    },
  })

  // Отдаём функцию применения барьеру перед входом (модульный синглтон выше).
  updateServiceWorker = updateSW

  const isIdle = (phase: string) => phase === 'Exited' || phase === 'PreJoin'

  watch(() => lifecycle.phase, (phase, prevPhase) => {
    // Применить отложенное обновление при выходе из конференции
    if (pendingUpdate && isIdle(phase)) {
      console.log('[SW] Applying deferred update')
      updateSW(true)
      return
    }

    // Человек не в звонке — перезагрузка бесплатна, проверяем обновление.
    // Только при входе в покой из звонка: Exited→PreJoin (сброс) не дублирует проверку
    if (isIdle(phase) && !isIdle(prevPhase)) {
      checkForUpdate('lifecycle')
      return
    }

    // Переподключение сигналинга: conf перевыкатили → сменилась версия бэкенда,
    // почти всегда рядом с выкаткой фронтенда
    if (phase === 'Connected' && prevPhase === 'Reconnecting') {
      checkForUpdate('signaling reconnect')
    }
  })
}
